import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import PageHead from "@/components/feature/PageHead";

const COMPANY_NAME = "NetBasis International";

const title = "Geschäftspräsenz";
const h1Text = "Ihr strategischer Partner für eine starke lokale Geschäftspräsenz in der DACH-Region. Mit einer Repräsentanz und Adresse in Düsseldorf positionieren wir Ihr ITK-Unternehmen hervorragend, rechtssicher und kosteneffizient im Markt.";
const heroImage = "https://net-basis.net/Bilder/duesseldorf-media-harbor-rhine-tower-business-location.webp";
const heroAlt = "Medienhafen Düsseldorf mit Rheinturm steht für eine lokale Geschäftspräsenz im DACH-Markt.";
const heroClaimLine1 = "Wirtschaftsstandort";
const heroClaimLine2 = "Düsseldorf";
const benefitsTitle = "Wie Sie von unserer Expertise im Bereich __Geschäftspräsenz__ profitieren:";
const p1 = "Für viele Unternehmen sind sowohl der Eintritt in neue Märkte als auch deren erfolgreiche Erschließung mit hohem Aufwand verbunden. Zudem werden regelmäßig die Befindlichkeiten, unterschiedlichen Sichtweisen oder Erwartungen potenzieller internationaler Geschäftspartner unterschätzt. Diese bevorzugen meist einen lokalen Ansprechpartner, der ihre Sprache spricht und dem die regionalen Lebensgewohnheiten, ihr Landesrecht und ihre Handelspraktiken geläufig sind. Fehlende Markt-, Landes- und Sprachkenntnisse sowie unzureichende personelle oder finanzielle Ressourcen stellen erhebliche Hürden dar.";
const p2Text = "Genau hier hilft NetBasis International mit der Dienstleistung \"Geschäftspräsenz\" als Bindeglied zwischen Ihrem Unternehmen und den neuen Märkten, alle potenziellen Schwierigkeiten oder Hindernisse wirksam zu meistern.";
const p2Parts = p2Text.split(COMPANY_NAME);
const p3 = "Mit einer lokalen Geschäftspräsenz und Adresse in Düsseldorf, die nicht nur Ihren Firmennamen hochhält oder Ihnen eine örtliche Telefonnummer und Kontaktperson bietet, sondern auch Ihr komplettes Geschäft in Ihrem Namen durchführt, sind Sie hervorragend und kosteneffizient positioniert.";
const bottomClaim = "NetBasis International - drive to excellence with excellent drivers !";

const benefits = [
  "Konsequente Minimierung von Kosten, operativem Aufwand, Zeit und Risiko",
  "Keine Notwendigkeit für eine eigene rechtliche Präsenz oder Firmierung vor Ort",
  "Unmittelbare und flexible Markteinstiegsmöglichkeiten",
  "Günstige Chance für einen fundierten Markt- und Produkttest",
  "Kein eigenes Personal oder administrative Infrastruktur erforderlich",
  "Effizienteres Follow-up bei bereits bestehenden Geschäftskontakten",
  "Nachhaltige Entlastung Ihrer eigenen Exportabteilung",
  "Vollständiger Abbau von Sprachbarrieren durch direkte und einfache Kommunikation",
  "Sofortiger Zugriff auf Datenbanken, Adresslisten sowie die Einbindung in Netzwerke",
  "Optimaler Einstieg oder nahtloser Übergang zu einer eigenen lokalen Niederlassung",
];

export default function GeschaeftspraesenzDePage() {
  return (
    <div className="min-h-screen flex flex-col">
      <PageHead
        title="Geschäftspräsenz | NetBasis International"
        description="Ihre lokale Niederlassung im DACH-Markt ohne administrativen Aufwand – professionell, präsent und direkt handlungsfähig."
        ogUrl="https://net-basis.net/de/services/business-development/geschaeftspraesenz"
      />
      <Header />

      <main className="flex-1 pt-20 md:pt-24">
        {/* Hero image */}
        <section className="w-full bg-white py-4 md:py-6 lg:py-8">
          <div className="w-full max-w-[1100px] mx-auto">
            <div className="relative w-full h-[320px] md:h-[420px] lg:h-[500px] overflow-hidden">
              <img loading="lazy"
                src={heroImage}
                alt={heroAlt}
                title={title}
                className="absolute inset-0 w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/50"></div>

              <div className="absolute inset-0 z-20 flex items-start justify-start pt-[22%] md:pt-[10%] lg:pt-[12%] pl-[8%] md:pl-[10%] pointer-events-none">
                <p
                  className="text-3xl md:text-4xl lg:text-5xl font-normal text-white drop-shadow-[0_1px_4px_rgba(0,0,0,0.6)] text-center leading-[1.15] md:leading-[1.5] animate-[trainingTextSlide_12s_ease-in-out_infinite]"
                  style={{
                    fontFamily: '"Open Sans", sans-serif',
                  }}
                >
                  <span className="block">{heroClaimLine1}</span>
                  {heroClaimLine2 && <span className="block">{heroClaimLine2}</span>}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Content */}
        <section className="py-6 md:py-8 px-4 md:px-6 lg:px-8 bg-white">
          <div className="max-w-5xl mx-auto">
            <h2 className="font-heading text-lg md:text-xl font-bold text-primary-600 mb-5 md:mb-7">
              {title}
            </h2>

            <h1 className="font-semibold text-foreground-950 text-sm md:text-base mb-4">
              {h1Text}
            </h1>

            <div className="space-y-4 text-foreground-950 leading-relaxed text-sm md:text-base">
              <p>
                {(() => {
                  const parts = p1.split(COMPANY_NAME);
                  return parts.map((part, i) => (
                    <span key={i}>
                      {part}
                      {i < parts.length - 1 && (
                        <strong className="text-primary-600">{COMPANY_NAME}</strong>
                      )}
                    </span>
                  ));
                })()}
              </p>
              <p>
                {p2Parts.map((part, i) => (
                  <span key={i}>
                    {part}
                    {i < p2Parts.length - 1 && (
                      <strong className="text-primary-600">{COMPANY_NAME}</strong>
                    )}
                  </span>
                ))}
              </p>
              <p>{p3}</p>
            </div>

            {/* Green heading with italic word */}
            <h2 className="font-heading text-lg md:text-xl font-bold text-primary-600 mt-8 md:mt-10 mb-5 md:mb-7">
              {benefitsTitle.split("__").map((part, i) =>
                i % 2 === 1 ? <em key={i}>{part}</em> : part
              )}
            </h2>

            {/* Benefits with green checkmarks */}
            <div className="space-y-1.5 md:space-y-2 text-foreground-950 leading-relaxed text-sm md:text-base">
              {benefits.map((benefit, idx) => (
                <div key={idx} className="flex items-start gap-2 md:gap-3">
                  <div className="w-5 h-5 flex-shrink-0 flex items-center justify-center mt-0.5">
                    <i className="ri-check-line text-primary-600 text-base md:text-lg"></i>
                  </div>
                  <p>{benefit}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Bottom claim */}
        <section className="py-6 md:py-10 px-4 md:px-6 lg:px-8 bg-white">
          <div className="max-w-5xl mx-auto text-center">
            <p className="font-heading text-lg md:text-xl font-bold text-primary-600">
              {bottomClaim}
            </p>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}